"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useEffect, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from "@/components/ui/carousel";
import { Progress } from "@/components/ui/progress";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const testimonials = [{
  id: 1,
  author: "Maria G.",
  role: "Parent",
  quote: "ABA With Passion Inc has transformed my child's life with their caring and professional approach.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1541246/img/caregiver_maria_testimonial.png"
}, {
  id: 2,
  author: "Carlos M.",
  role: "Caregiver",
  quote: "The team is bilingual and understanding, making it easier for my family to access the services we need.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1541246/img/caregiver_carlos_testimonial.png"
}, {
  id: 3,
  author: "Sarah L.",
  role: "Parent",
  quote: "The in-home services have been a game changer for our family. Professional, compassionate, and effective.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1541246/img/caregiver_sarah_testimonial.png"
}];
export default function Testimonials() {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [progress, setProgress] = useState(0);
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    once: true,
    threshold: 0.2
  });
  useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);
  useEffect(() => {
    const progressValue = (current + 1) / testimonials.length * 100;
    setProgress(progressValue);
  }, [current]);
  return <section id="testimonials" ref={ref} className="dark bg-background text-foreground py-16 lg:py-24 px-4">
      <div className="max-w-5xl mx-auto">
        <div className={`text-center mb-12 transition-all duration-[var(--duration-medium)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-[10px]"}`}>
          <h2 className="text-3xl lg:text-4xl font-display font-bold tracking-tight mb-4">
            What Families Say
          </h2>
          <p className="text-muted text-lg max-w-2xl mx-auto">
            Hear from the families we've had the privilege to support on their
            journey.
          </p>
        </div>

        <Carousel setApi={setApi} opts={{
        align: "center",
        loop: true
      }} className={`w-full transition-all duration-[var(--duration-medium)] delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-[10px]"}`}>
          <CarouselContent>
            {testimonials.map((testimonial, i) => <CarouselItem key={testimonial.id} data-index={i}>
                <div className="p-1">
                  <div className="bg-dark-background/50 backdrop-blur-sm border border-border/20 rounded-[var(--radius-lg)] p-8 lg:p-12 min-h-[280px] flex flex-col items-center justify-center text-center transition-all duration-[var(--duration-medium)]">
                    <SafeHydrate><Avatar className="size-16 mb-6 ring-2 ring-primary/20">
                      <AvatarImage src={testimonial.image} alt={testimonial.author} />
                      <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                        {testimonial.author.charAt(0)}
                      </AvatarFallback>
                    </Avatar></SafeHydrate>

                    <blockquote className="text-lg lg:text-xl text-foreground/90 italic mb-6 leading-relaxed max-w-2xl">
                      "{testimonial.quote}"
                    </blockquote>

                    <div>
                      <p className="font-semibold text-foreground">
                        {testimonial.author}
                      </p>
                      <p className="text-sm text-muted">{testimonial.role}</p>
                    </div>
                  </div>
                </div>
              </CarouselItem>)}
          </CarouselContent>

          <CarouselPrevious className="hidden lg:flex -left-16 bg-primary/10 border-primary/20 text-primary hover:bg-primary hover:text-foreground-primary transition-all duration-[var(--duration-fast)]" />
          <CarouselNext className="hidden lg:flex -right-16 bg-primary/10 border-primary/20 text-primary hover:bg-primary hover:text-foreground-primary transition-all duration-[var(--duration-fast)]" />
        </Carousel>

        <div className="mt-8 max-w-md mx-auto">
          <Progress value={progress} className="h-1 bg-border/30" />
          <div className="flex justify-center items-center gap-2 mt-4">
            {testimonials.map((_, index) => <button key={index} onClick={() => api?.scrollTo(index)} className={`size-2 rounded-full transition-all duration-[var(--duration-fast)] ${current === index ? "bg-primary w-6" : "bg-border/50 hover:bg-border"}`} aria-label={`Go to testimonial ${index + 1}`} />)}
          </div>
        </div>
      </div>
    </section>;
}